import { cn } from '@/utils/cn';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  FUNDING_RATE_DECIMALS,
  useMarketStore,
} from '@/store/use-markets-store';
import { useStore } from 'zustand';
import {
  bigIntToDecimalStr,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { useTickerLatest } from '@/features/markets/api/get-ticker-latest';
import { useTicker24hr } from '@/features/markets/api/get-ticker-24hr';
import useOpenInterest from '@/features/markets/api/get-open-interest';
import TooltipWrapper from '@/components/ui/tooltip';
import { ChevronDown } from 'lucide-react';

interface IMarketInfoProps {}

export const MarketInfo: React.FC<IMarketInfoProps> = ({}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { symbol } = useParams() as { symbol: string };
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  // useTickerLatest({ symbols: [symbol] });
  useTickerLatest({}); // Temp query all symbols, TODO: cache by symbol
  useTicker24hr({ symbols: [symbol] });
  useOpenInterest({ symbols: [symbol] });
  // TODO: maybe check if ws is connected here? if not display the useTickerLatest data

  const { marketData, marketSpec } = useStore(
    useMarketStore(symbol),
    (state) => state,
  );

  const data = React.useMemo(
    () => ({
      markPrice: bigIntToDecimalStr(
        marketData.markPrice,
        marketSpec.priceDecimals,
      ),
      indexPrice: bigIntToDecimalStr(
        marketData.indexPrice,
        marketSpec.priceDecimals,
      ),
      priceChange: bigIntToDecimalStr(
        marketData.priceChange,
        marketSpec.priceDecimals,
      ),
      priceChangePct: bigIntToDecimalStr(marketData.priceChangePct, 4),
      oneHrFundingRate: bigIntToDecimalStr(
        marketData.oneHrFundingRate * 100n,
        FUNDING_RATE_DECIMALS,
      ),
      fundingApy: bigIntToDecimalStr(
        marketData.oneHrFundingRate * 24n * 365n * 100n,
        FUNDING_RATE_DECIMALS,
      ),
      ...(typeof marketData.openInterest === 'bigint'
        ? {
            openInterest: bigIntToDecimalStr(
              marketData.openInterest,
              marketSpec.priceDecimals + marketSpec.sizeDecimals,
            ),
          }
        : {}),
      ...(typeof marketData.quoteVolume === 'bigint'
        ? {
            quoteVolume: bigIntToDecimalStr(
              marketData.quoteVolume,
              marketSpec.priceDecimals + marketSpec.sizeDecimals,
            ),
          }
        : {}),
    }),
    [marketData, marketSpec],
  );

  const items = [
    {
      title: 'Mark Price',
      value: formatNumber(data?.markPrice, {
        digits: Number(marketSpec.priceDecimals),
      }),
      color: 'text-vestgrey-50',
      tooltip:
        'Determined by risk-based pricing. Used for calculating unrealized PnL and triggering TP/SL orders.',
    },
    {
      title: 'Index Price',
      value: formatNumber(data?.indexPrice, {
        digits: Number(marketSpec.priceDecimals),
      }),
      color: 'text-vestgrey-50',
      tooltip: 'Weighted average of spot prices on external venues.',
    },
    {
      title: '24h Change',
      value: `${formatNumber(data?.priceChange, { digits: Number(marketSpec.priceDecimals), showChange: true })} / ${formatNumber(data?.priceChangePct, { style: 'percent', digits: 2, abs: true })}`,
      color:
        data?.priceChange === undefined
          ? 'text-vestgrey-400'
          : Number(data?.priceChange) >= 0n
            ? 'text-green'
            : 'text-red',
    },
    {
      title: '1h Funding',
      value: `${formatNumber(data?.oneHrFundingRate, { style: 'percent', digits: Number(FUNDING_RATE_DECIMALS) - 2 })}`,
      color:
        data?.oneHrFundingRate === undefined
          ? 'text-vestgrey-400'
          : Number(data?.oneHrFundingRate) >= 0n
            ? 'text-green'
            : 'text-red',
      tooltip:
        'Defined based on marginal contribution to system-wide risk. Charged continuously and settled upon order execution.',
    },
    {
      title: 'Funding APY',
      value: `${formatNumber(data?.fundingApy, { style: 'percent', digits: 0 })}`,
      color:
        data?.fundingApy === undefined
          ? 'text-vestgrey-400'
          : Number(data?.fundingApy) >= 0n
            ? 'text-green'
            : 'text-red',
    },
    {
      title: 'Open Interest',
      value: formatNumber(data?.openInterest, {
        digits: 2,
      }),
      color: 'text-vestgrey-50',
    },
    {
      title: '24H Volume',
      value: formatNumber(data?.quoteVolume, {
        digits: 2,
      }),
      color: 'text-vestgrey-50',
    },
  ];

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="w-full">
      <button
        className="flex h-full w-full items-center justify-center gap-2 px-4 font-mono sm:gap-4 sm:px-6 lg:hidden"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="font-bold">{formatNumber(data.markPrice)}</span>
        <span
          className={cn(
            'text-sm',
            Number(data.priceChange) >= 0n ? 'text-green' : 'text-red',
          )}
        >
          <span className="hidden sm:inline">
            {formatNumber(data?.priceChange, {
              digits: Number(marketSpec.priceDecimals),
              showChange: true,
            })}{' '}
            /{' '}
          </span>
          <span className="sm:hidden">
            {Number(data.priceChange) >= 0n ? '+' : '-'}
          </span>
          {formatNumber(data?.priceChangePct, {
            style: 'percent',
            digits: 2,
            abs: true,
          })}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-vestgrey-100 transition-transform',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>
      <div
        className={cn(
          'absolute inset-x-0 top-full z-40 grid h-max w-full max-w-7xl grid-cols-2 items-center justify-between gap-4 border-y border-border bg-background px-6 py-4 transition-opacity sm:grid-cols-3 sm:px-8 lg:static lg:flex lg:h-full lg:gap-0 lg:border-y-0 lg:py-0',
          isOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0 lg:pointer-events-auto lg:opacity-100',
        )}
      >
        {items.map((item) => (
          <div
            className={cn(
              'flex flex-col items-center gap-1 lg:items-start lg:gap-0.5',
              item.title === 'Funding APY' && 'hidden lg:flex',
              // item.title === '24h Change' ? 'min-w-44' : 'min-w-36',
            )}
            key={item.title}
          >
            {item.tooltip ? (
              <TooltipWrapper content={item.tooltip}>
                <h4 className="max-w-max select-none font-mono text-sm capitalize text-vestgrey-600 underline">
                  {item.title.toUpperCase()}
                </h4>
              </TooltipWrapper>
            ) : (
              <h4 className="font-mono text-sm capitalize text-vestgrey-600">
                {item.title.toUpperCase()}
              </h4>
            )}
            <div
              className={cn(
                'flex flex-1 items-center justify-start text-xl font-bold text-vestgrey-200',
                item.color,
              )}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
